import delay from 'delay';
import { delegate } from '../utils/tools';
import hoverintent from 'hoverintent';
import * as preact from 'preact';

import { prefixUrl, linkOrLabel, Icon } from './mobile';
import * as wpConfig from '../config/wp-settings';

let activeDropdowns = Object.create(null);
let dropdowns = Object.create(null);
let header = null;

let hoverintentOptions = {
	sensitivity: 9,
	interval: 25,
};

let menuImages = {
	About: 'https://www.stolaf.edu/images/sto-menu-about.jpg',
	Academics: 'https://www.stolaf.edu/images/sto-menu-academics.jpg',
	Admissions: 'https://www.stolaf.edu/images/sto-menu-admissions.jpg',
	'On the Hill': 'https://www.stolaf.edu/images/sto-menu-onthehill.jpg',
};

let menuData = wpConfig.GLOBAL_NAV.menu_items;

function GlobalNavMenu({ menu }) {
	let { meta, label } = menu;

	let wrapperClass = [
		'l-wrapper',
		'global-nav__dropdown-wrapper',
		meta.image.src && 'has-image',
		meta.content && 'has-content',
	]
		.filter(cls => cls !== false)
		.join(' ');

	// forcibly set menu images
	let imgSrc = menuImages[label] || meta.image.src;

	return (
		<article
			className="global-nav__dropdown"
			hidden={true}
			data-id={menu.menu_id}
			data-js="global-nav-dropdown"
		>
			<div className={wrapperClass}>
				{meta.image && meta.image.src ? (
					<img
						className="global-nav__dropdown-image"
						src={imgSrc}
						alt={meta.image.title}
					/>
				) : null}

				{meta.content ? (
					<p
						className="global-nav__dropdown-content"
						dangerouslySetInnerHTML={{ __html: meta.content }}
					/>
				) : null}

				<div className="global-nav__dropdown-links">
					{menu.menu_items.map(item => (
						<GlobalNavItemColumn item={item} />
					))}
				</div>
			</div>
		</article>
	);
}

function GlobalNavItemColumn({ item }) {
	let label = linkOrLabel(item);

	return (
		<div className="global-nav__dropdown-column">
			{label ? (
				<h3 className="global-nav__dropdown-subheading">{label}</h3>
			) : null}

			{item.menu_items.length ? (
				<ul className="global-nav__dropdown-list">
					{item.menu_items.map(subitem => (
						<GlobalNavSubItem item={subitem} />
					))}
				</ul>
			) : null}
		</div>
	);
}

function GlobalNavSubItem({ item }) {
	let url = prefixUrl(item.url);

	return (
		<li className="global-nav__dropdown-list-item">
			<a className="global-nav__dropdown-list-link" href={url}>
				<Icon name="chevron-right" />
				<span
					className="global-nav__dropdown-list-text"
					dangerouslySetInnerHTML={{ __html: item.label }}
				/>
			</a>
		</li>
	);
}

/**
 * @function getTarget
 * @description Hoverintent and the inner spans for accessibility means we have variable event target
 * based on mouse entry/leave
 */

function getTarget(e) {
	let parent = !e.target.matches('.menu-item--depth-0')
		? e.target.closest('.menu-item--depth-0')
		: e.target;
	return parent.querySelector('[data-js=trigger-global-dropdown]');
}

function revealDropdown(link, id) {
	activeDropdowns[id] = true;

	header.classList.add('global-nav__dropdown--active');

	dropdowns[id].classList.remove('global-nav__dropdown--hidden');
	dropdowns[id].removeAttribute('hidden');

	delay(100).then(() => link.classList.add('dropdown-active'));
}

function hideDropdown(link, id) {
	delete activeDropdowns[id];
	dropdowns[id].setAttribute('hidden', '');
	link.classList.remove('dropdown-active');

	if (Object.keys(activeDropdowns).length === 0) {
		header.classList.remove('global-nav__dropdown--active');
	}
}

function hoverIn(e) {
	let target = getTarget(e);
	if (!target) {
		return;
	}

	let id = parseInt(target.dataset.id, 10);

	let data = menuData.filter(menu => menu['data-id'] === id)[0];

	let wrapper = e.target.closest('.menu-item--depth-0');
	let link = wrapper.querySelector('[data-js=trigger-global-dropdown]');

	let idExists = id in dropdowns;
	if (!idExists) {
		preact.render(<GlobalNavMenu menu={data} />, wrapper);

		let selector = '[data-js=global-nav-dropdown]';
		let dropdownNode = wrapper.querySelector(selector);

		if (dropdownNode == null) {
			console.warn(
				`did not find node matching ${selector} within`,
				target.parentNode
			);
		}

		dropdowns[id] = dropdownNode;
	}

	revealDropdown(link, id);
}

function hoverOut(e) {
	let target = getTarget(e);
	if (!target) {
		return;
	}

	let targetId = parseInt(target.dataset.id, 10);

	let wrapper = e.target.closest('.menu-item--depth-0');
	let link = wrapper.querySelector('[data-js=trigger-global-dropdown]');

	delay(200).then(() => hideDropdown(link, targetId));
}

function handleDropdownKeyEvents(e) {
	if (e.which !== 27) {
		return;
	}

	let targetId = parseInt(e.delegateTarget.dataset.id, 10);

	let wrapper = e.target.closest('.menu-item--depth-0');
	let link = wrapper.querySelector('[data-js=trigger-global-dropdown]');

	delay(200).then(() => hideDropdown(link, targetId));
	e.delegateTarget.previousElementSibling.focus();
}

function handleDropdownFocus(e) {
	let isLink =
		e.relatedTarget &&
		e.relatedTarget.matches('.global-nav__dropdown-list-link');

	if (isLink) {
		return;
	}

	let targetId = parseInt(e.delegateTarget.dataset.id, 10);
	let wrapper = e.target.closest('.menu-item--depth-0');
	let link = wrapper.querySelector('[data-js=trigger-global-dropdown]');
	delay(200).then(() => hideDropdown(link, targetId));
}

function handleClickEvents(e) {
	if (!e.delegateTarget.matches('.dropdown-active')) {
		e.preventDefault();
	}
}

function handleKeyEvents(e) {
	if (e.which !== 13) {
		return;
	}

	e.preventDefault();
	if (!e.delegateTarget.nextElementSibling) {
		hoverIn(e);
		return;
	}

	let nextIsHidden = e.delegateTarget.nextElementSibling.matches('[hidden]');
	if (nextIsHidden) {
		hoverIn(e);
		return;
	}
	hoverOut(e);
}

export default function init() {
	if (typeof menuData.filter !== 'function') {
		return;
	}

	let container = document.querySelector('[data-js=global-nav]');

	if (!container) {
		return;
	}

	header = document.querySelector('[data-js=site-header]');

	// bind events
	delegate(
		container,
		'[data-js="trigger-global-dropdown"]',
		'keydown',
		handleKeyEvents
	);
	delegate(
		container,
		'[data-js="trigger-global-dropdown"]',
		'click',
		handleClickEvents
	);

	delegate(
		container,
		'[data-js="global-nav-dropdown"]',
		'keydown',
		handleDropdownKeyEvents
	);
	delegate(
		container,
		'[data-js="global-nav-dropdown"]',
		'focusout',
		handleDropdownFocus
	);

	Array.prototype.forEach.call(
		container.querySelectorAll('.menu-item--depth-0'),
		item => hoverintent(item, hoverIn, hoverOut).options(hoverintentOptions)
	);

	// done
	console.info('Initialized global navigation scripts');
}
